import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";

const GENDERS = [
  { v: "all", l: "All" },
  { v: "women", l: "Women" },
  { v: "men", l: "Men" },
];
const CATEGORIES = [
  { v: "all", l: "All" },
  { v: "braids", l: "Braids" },
  { v: "twists", l: "Twists" },
  { v: "locs", l: "Locs" },
  { v: "fade", l: "Fades" },
  { v: "natural", l: "Natural" },
  { v: "weaves", l: "Weaves" },
  { v: "wigs", l: "Wigs" },
  { v: "short", l: "Short" },
  { v: "statement", l: "Statement" },
];

export default function Gallery() {
  const [params, setParams] = useSearchParams();
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);

  const gender = params.get("gender") || "all";
  const category = params.get("category") || "all";

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (gender !== "all") q.gender = gender;
    if (category !== "all") q.category = category;
    api.get("/hairstyles", { params: q })
      .then((r) => setStyles(r.data.hairstyles || []))
      .finally(() => setLoading(false));
  }, [gender, category]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "all") next.delete(k); else next.set(k, v);
    setParams(next);
  };

  return (
    <main className="crown-container py-12 md:py-16" data-testid="gallery-page">
      <div className="mb-12">
        <p className="font-overline mb-3">The atelier index</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Browse every style.</h1>
      </div>

      {/* Filters */}
      <div className="space-y-4 mb-10">
        <div className="flex flex-wrap items-center gap-2" data-testid="gender-filter">
          <p className="font-overline text-ash mr-2">Gender:</p>
          {GENDERS.map((g) => (
            <button
              key={g.v}
              data-testid={`gallery-gender-${g.v}`}
              onClick={() => setFilter("gender", g.v)}
              className={`px-4 py-2 font-overline text-xs border transition-colors ${
                gender === g.v ? "border-gold text-gold bg-gold/5" : "border-bone/15 text-ash hover:text-bone"
              }`}
            >
              {g.l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="category-filter">
          <p className="font-overline text-ash mr-2">Category:</p>
          {CATEGORIES.map((c) => (
            <button
              key={c.v}
              data-testid={`gallery-cat-${c.v}`}
              onClick={() => setFilter("category", c.v)}
              className={`px-4 py-2 font-overline text-xs border transition-colors ${
                category === c.v ? "border-gold text-gold bg-gold/5" : "border-bone/15 text-ash hover:text-bone"
              }`}
            >
              {c.l}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] bg-obsidian animate-pulse" />)}
        </div>
      ) : styles.length === 0 ? (
        <p className="font-overline text-ash py-20 text-center" data-testid="gallery-empty">No styles match.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {styles.map((s, idx) => (
            <Link
              key={s.id}
              to={`/style/${s.id}`}
              className="group relative aspect-[3/4] overflow-hidden"
              data-testid={`gallery-card-${s.id}`}
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <img src={s.image_url} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/85 via-espresso/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-overline text-gold text-[0.65rem]">{s.gender} · {s.category}</p>
                <p className="font-serif text-2xl mt-1 leading-tight">{s.name}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
